import axios from "axios";
import React from "react";
import { useState, useEffect } from "react";

import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";

import FormRowState from "../UtilityComponents/FormUtilityComponents/FormRowState";
import FormConfig from "../UtilityComponents/FormUtilityComponents/FormConfig";

import { Divider, FormControl, TextField, InputAdornment, Tooltip, Typography } from "@mui/material";
import NumericFormatComponent from "../UtilityComponents/FormUtilityComponents/NumericFormatComponent";
import Box from "@mui/material/Box";

import Collapse from "@mui/material/Collapse";

import { MenuItem } from "@mui/material";
import Slider from "@mui/material/Slider";

import { useTheme } from "@mui/material/styles";
import { AutoFixHigh } from "@mui/icons-material";
import LearnTextEmbed from "../LearnPanelComponents/LearnCopy/LearnTextEmbed";
import MyCountUp from "../UtilityComponents/NumberDisplayComponents/MyCountUp";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";

import CallToActionBox from "../UtilityComponents/PaperUtilityComponents/CallToActionBox";
import { Paper } from "@mui/material";
import PagePaper from "../../PageComponents/PagePaper";
import dayjs from "dayjs";
import PageOption from "../../PageTypes/PageOption";
import { isMobile } from "react-device-detect";

import ESPPPurchasePeriodType from "./ESPPPurchasePeriodType";
import getNextPurchasePeriod from "./getNextPurchasePeriod";

const paperPadding = 2;
const countUpDuration = 0.75;

function shortDateString(date: dayjs.Dayjs | undefined) {
  if (date === undefined) {
    return "-";
  }
  return date.format("MMM D, YYYY");
}

function longDateString(date: dayjs.Dayjs | undefined) {
  if (date === undefined) {
    return "-";
  }
  return date.format("MMMM D, YYYY");
}

function getValueGainStringPlanFeatures(discount: number, lookback: boolean) {
  if (discount > 0) {
    if (lookback) {
      return Math.round(discount) + "% discount and lookback";
    } else {
      return Math.round(discount) + "% discount";
    }
  } else if (lookback) {
    return "lookback";
  } else {
    return "the ESPP";
  }
}

function periodToMenuOptionString(period: ESPPPurchasePeriodType) {
  const offering_date = period.offering_date;
  const purchase_date = period.purchase_date;
  const isFutureStart = offering_date.isAfter(dayjs());
  const isFutureEnd = purchase_date.isAfter(dayjs());
  if (isFutureStart) {
    return longDateString(offering_date) + " to " + longDateString(purchase_date);
  } else if (isFutureEnd) {
    return "[IN PROCESS] " + longDateString(offering_date) + " to " + longDateString(purchase_date);
  } else {
    return "[PAST] " + longDateString(offering_date) + " to " + longDateString(purchase_date);
  }
}

function offeringDateToMenuOptionString(offering_date: dayjs.Dayjs) {
  const isFutureStart = offering_date.isAfter(dayjs());
  if (isFutureStart) {
    return longDateString(offering_date);
  } else {
    return "[PAST] " + longDateString(offering_date);
  }
}

function periodToOfferingDateString(period: ESPPPurchasePeriodType) {
  return offeringDateToMenuOptionString(period.offering_date);
}

function periodToPurchaseWindowString(period: ESPPPurchasePeriodType) {
  const isFutureStart = period.purchase_start_date.isAfter(dayjs());
  const isFutureEnd = period.purchase_date.isAfter(dayjs());
  if (isFutureStart) {
    return longDateString(period.purchase_start_date) + " to " + longDateString(period.purchase_date);
  } else if (isFutureEnd) {
    return "[IN PROCESS] " + longDateString(period.purchase_start_date) + " to " + longDateString(period.purchase_date);
  } else {
    return "[PAST] " + longDateString(period.purchase_start_date) + " to " + longDateString(period.purchase_date);
  }
}

type ESPPExplorerPanelProps = {
  addSnackbarMessage: (title: string, message: string, severity: "success" | "info" | "warning" | "error") => void;
  setPage: (newPage: PageOption) => void;
  ESPPPercent: number;
  setESPPPercent: (newPercent: number) => void;
  confirmedESPPPercent: number;
  setConfirmedESPPPercent: (newPercent: number) => void;
  ESPPPeriods: ESPPPurchasePeriodType[];
  period: ESPPPurchasePeriodType | undefined;
  setPeriod: (newPeriod: ESPPPurchasePeriodType | undefined) => void;
  lastDrawnOfferingPrice: number | undefined;
  setLastDrawnOfferingPrice: (newPrice: number) => void;
  lastDrawnPurchasePrice: number | undefined;
  setLastDrawnPurchasePrice: (newPrice: number) => void;
  lastDrawnEffectivePurchasePrice: number | undefined;
  setLastDrawnEffectivePurchasePrice: (newValue: number) => void;
  lastDrawnStockValueAtPurchase: number | undefined;
  setLastDrawnStockValueAtPurchase: (newValue: number) => void;
  lastDrawnStockNum: number | undefined;
  setLastDrawnStockNum: (newValue: number) => void;
  lastDrawnCashContrib: number | undefined;
  setLastDrawnCashContrib: (newValue: number) => void;
  lastDrawnValueGained: number | undefined;
  setLastDrawnValueGained: (newValue: number) => void;
};

function ESPPExplorerPanel(props: ESPPExplorerPanelProps) {
  const theme = useTheme();

  const ESPPPercent = props.ESPPPercent;
  const setESPPPercent = props.setESPPPercent;
  const confirmedESPPPercent = props.confirmedESPPPercent;
  const setConfirmedESPPPercent = props.setConfirmedESPPPercent;
  const ESPPPeriods = props.ESPPPeriods;
  const period = props.period;
  const setPeriod = props.setPeriod;
  const lastDrawnOfferingPrice = props.lastDrawnOfferingPrice;
  const setLastDrawnOfferingPrice = props.setLastDrawnOfferingPrice;
  const lastDrawnPurchasePrice = props.lastDrawnPurchasePrice;
  const setLastDrawnPurchasePrice = props.setLastDrawnPurchasePrice;
  const lastDrawnStockValueAtPurchase = props.lastDrawnStockValueAtPurchase;
  const lastDrawnEffectivePurchasePrice = props.lastDrawnEffectivePurchasePrice;
  const setLastDrawnEffectivePurchasePrice = props.setLastDrawnEffectivePurchasePrice;
  const setLastDrawnStockValueAtPurchase = props.setLastDrawnStockValueAtPurchase;
  const lastDrawnStockNum = props.lastDrawnStockNum;
  const setLastDrawnStockNum = props.setLastDrawnStockNum;
  const lastDrawnCashContrib = props.lastDrawnCashContrib;
  const setLastDrawnCashContrib = props.setLastDrawnCashContrib;
  const lastDrawnValueGained = props.lastDrawnValueGained;
  const setLastDrawnValueGained = props.setLastDrawnValueGained;

  const salary = 140000;
  const maxContributionPercent = 15;

  const nextPurchasePeriod = getNextPurchasePeriod(ESPPPeriods, dayjs());
  const discount = period === undefined ? 15 : period.discount_rate;
  const lookback = period === undefined ? true : period.has_lookback;

  const isFutureOffering = period === undefined ? true : period.offering_date.isAfter(dayjs());
  const isFuturePurchase = period === undefined ? true : period.purchase_date.isAfter(dayjs());
  const offeringStockPrice =
    period === undefined
      ? -1
      : isFutureOffering
      ? period.current_stock_price
      : period.offering_stock_price === undefined
      ? -1
      : period.offering_stock_price;
  const purchaseStockPrice =
    period === undefined
      ? -1
      : isFuturePurchase
      ? period.current_stock_price
      : period.purchase_stock_price === undefined
      ? -1
      : period.purchase_stock_price;

  //TODO: unhardcode this divide by 2 for how much salary is paid in a period
  const cashContrib = Math.round(((salary / 2) * ESPPPercent) / 100);

  const effectivePurchasePrice = Math.min(offeringStockPrice, purchaseStockPrice) * (1 - discount / 100);

  //TODO: this assumes lookback is always true
  const stockNum = Math.round(cashContrib / effectivePurchasePrice);

  const stockNumCountUp = (
    <MyCountUp
      value={stockNum}
      lastFullyDrawnValue={lastDrawnStockNum}
      setLastFullyDrawnValue={setLastDrawnStockNum}
      prefix={isFuturePurchase ? " could be the value of " : " is the value of "}
      suffix={" shares purchased on " + shortDateString(period?.purchase_date) + (isFuturePurchase ? "*" : "")}
      decimals={0}
      duration={countUpDuration}
      negativeIsError
    />
  );

  const stockValueAtPurchase = Math.round(stockNum * purchaseStockPrice);
  const stockValueAtPurchaseCountUp = (
    <MyCountUp
      value={stockValueAtPurchase}
      lastFullyDrawnValue={lastDrawnStockValueAtPurchase}
      setLastFullyDrawnValue={setLastDrawnStockValueAtPurchase}
      prefix={"$"}
      suffix={""}
      decimals={0}
      duration={countUpDuration}
      negativeIsError
    />
  );

  const numMarks = 4;

  const marks = Array.from({ length: numMarks }, (_, i) => {
    const value = Math.round((maxContributionPercent / (numMarks - 1)) * i);
    return {
      value,
      label: `${value}%` + (i === numMarks - 1 ? " (max)" : ""),
    };
  });

  const ExampleSlider = (
    <Slider
      value={ESPPPercent}
      onChange={(e, newValue) => {
        setESPPPercent(newValue as number);
      }}
      step={1}
      valueLabelDisplay="auto"
      marks={marks}
      min={0}
      max={maxContributionPercent}
      sx={{ width: 300, pt: 2 }}
    />
  );

  const explainText = (
    <>
      <LearnTextEmbed
        content={
          "At the end of each purchase period, your contributions are used to buy Coinbase stock. Thanks to your ESPP's lookback feature, you buy stock at the lower of: "
        }
      />
      <br />
      <Typography component="span" fontWeight="bold">
        <MyCountUp
          value={offeringStockPrice}
          lastFullyDrawnValue={lastDrawnOfferingPrice}
          setLastFullyDrawnValue={setLastDrawnOfferingPrice}
          prefix={"• $"}
          suffix={isFutureOffering ? "*" : ""}
          decimals={2}
          duration={countUpDuration}
          negativeIsError
        />
      </Typography>
      <LearnTextEmbed
        content={
          ", the stock price at the start of the offering period on " +
          shortDateString(period?.offering_date as dayjs.Dayjs)
        }
      />
      <br />
      <Typography component="span" fontWeight="bold">
        <MyCountUp
          value={purchaseStockPrice}
          lastFullyDrawnValue={lastDrawnPurchasePrice}
          setLastFullyDrawnValue={setLastDrawnPurchasePrice}
          prefix={"• $"}
          suffix={isFuturePurchase ? "*" : ""}
          decimals={2}
          duration={countUpDuration}
          negativeIsError
        />
      </Typography>
      <LearnTextEmbed
        content={
          ", the stock price at the end of the purchase period on " +
          shortDateString(period?.purchase_date as dayjs.Dayjs)
        }
      />
      <br />
      {discount > 0 ? (
        <>
          <LearnTextEmbed content="And, even better, you get a " />
          <Typography component="span" fontWeight="bold">
            {Math.round(discount)}% discount
          </Typography>
          <LearnTextEmbed content=" on top of that! Making your purchase price " />
          <Typography component="span" fontWeight="bold">
            <MyCountUp
              value={effectivePurchasePrice}
              lastFullyDrawnValue={lastDrawnEffectivePurchasePrice}
              setLastFullyDrawnValue={setLastDrawnEffectivePurchasePrice}
              prefix={"$"}
              suffix={isFuturePurchase ? "*" : ""}
              decimals={2}
              duration={countUpDuration}
              negativeIsError
            />
          </Typography>
        </>
      ) : null}
    </>
  );

  function handleConfirm() {
    props.addSnackbarMessage(
      "ESPP Election Not Set",
      "This is just a sandbox environment. Your ESPP election has not been confirmed",
      "warning"
    );
    setConfirmedESPPPercent(ESPPPercent);
  }

  const cashContribString = isFuturePurchase
    ? "is how much cash you'd contribute from paychecks"
    : "was contributed from paychecks during window";
  const cashContribCountUp = (
    <MyCountUp
      value={cashContrib}
      lastFullyDrawnValue={lastDrawnCashContrib}
      setLastFullyDrawnValue={setLastDrawnCashContrib}
      prefix={"- $"}
      suffix={" " + cashContribString}
      decimals={0}
      duration={countUpDuration}
      negativeIsError
    />
  );

  const valueGainedStringPlanFeatures = getValueGainStringPlanFeatures(discount, lookback);
  const valueGainedString = isFuturePurchase
    ? "could be the value gained from " + valueGainedStringPlanFeatures + "*"
    : "is the value gained from " + valueGainedStringPlanFeatures;
  const valueGained = stockValueAtPurchase - cashContrib;
  const valueGainedCountUp = (
    <MyCountUp
      value={valueGained}
      lastFullyDrawnValue={lastDrawnValueGained}
      setLastFullyDrawnValue={setLastDrawnValueGained}
      prefix={"$"}
      suffix={" " + valueGainedString}
      decimals={0}
      duration={countUpDuration}
      negativeIsError
    />
  );

  const uniqueOfferingDates = Array.from(new Set(ESPPPeriods.map((period) => period.offering_date.toISOString()))).map(
    (date) => dayjs(date)
  );
  const currentPurchasePeriods =
    period === undefined ? [] : ESPPPeriods.filter((p) => p.offering_date.isSame(period.offering_date));

  const instruction_variant = "body1";
  const outcome_variant = "h6";

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, justifyContent: "flex-start", gap: 2 }}>
      <Paper
        sx={{
          backgroundColor: theme.palette.background.paper,
          padding: paperPadding,
          maxWidth: 1000,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h4" color="info.main">
          The Coinbase Employee Stock Purchase Plan (ESPP)
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, pt: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: "text",
            }}
          >
            <Typography component="span" variant={instruction_variant} sx={{ alignSelf: "flex-start" }}>
              <Typography component="span" variant="inherit" fontWeight="bold">
                Drag the slider
              </Typography>
              <LearnTextEmbed content=" to adjust what percent of each of paycheck is used to buy Coinbase stock as part of the ESPP." />
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 0 : 10,
                alignItems: "center",
                pt: isMobile ? 0 : 1,
              }}
            >
              {ExampleSlider}
              <Tooltip title="Annual salary API/editing features not available in this sandbox">
                <TextField
                  type="string" // Hack for now, use number and numeric format component later
                  value={salary.toLocaleString()}
                  disabled
                  label={"Annual Pay"}
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { min: 0 },
                  }}
                  sx={{ width: 110 }}
                />
              </Tooltip>
            </Box>
          </Box>
          <Typography component="span" variant={instruction_variant} sx={{ pt: isMobile ? 1 : 0 }}>
            <Typography component="span" variant="inherit" fontWeight="bold">
              <LearnTextEmbed content="Explore different offering dates and purchase periods" />
            </Typography>
            <LearnTextEmbed content=" to see how contributions can become worth more." />
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              pt: 1,
              width: "100%",
              justifyContent: "center",
            }}
          >
            <FormControl sx={{ pt: 1, flexGrow: 1, maxWidth: isMobile ? "100%" : "40%" }}>
              <TextField
                select
                label="Offering Date"
                value={period === undefined ? "" : periodToOfferingDateString(period)}
                onChange={(e) => {
                  const selectedPeriod = ESPPPeriods.find((p) => periodToOfferingDateString(p) === e.target.value);
                  setPeriod(selectedPeriod);
                }}
                SelectProps={{ MenuProps: { sx: { maxHeight: 500 } } }} // add this line
              >
                {uniqueOfferingDates.map((od, i) => {
                  const menuOptionString = offeringDateToMenuOptionString(od);
                  return (
                    <MenuItem key={i} value={menuOptionString}>
                      {menuOptionString}
                    </MenuItem>
                  );
                })}
              </TextField>
            </FormControl>
            <FormControl sx={{ pt: 1, flexGrow: 1, maxWidth: isMobile ? "100%" : "40%" }}>
              <TextField
                select
                label="Purchase Period"
                value={period === undefined ? "" : periodToPurchaseWindowString(period)}
                onChange={(e) => {
                  const selectedPeriod = currentPurchasePeriods.find(
                    (p) => periodToPurchaseWindowString(p) === e.target.value
                  );
                  setPeriod(selectedPeriod);
                }}
                SelectProps={{ MenuProps: { sx: { maxHeight: 500 } } }} // add this line
              >
                {currentPurchasePeriods.map((period, i) => {
                  const menuOptionString = periodToPurchaseWindowString(period);
                  return (
                    <MenuItem key={i} value={menuOptionString}>
                      {menuOptionString}
                    </MenuItem>
                  );
                })}
              </TextField>
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flexStart",
              px: 2,
              mt: 2,
              py: 2,
              //@ts-expect-error
              backgroundColor: theme.palette.background.dark,
              borderRadius: 2,
            }}
          >
            <Typography variant={outcome_variant} color="text">
              Summary of Estimated ESPP Outcome
            </Typography>
            <Typography variant={outcome_variant} color="green">
              {stockValueAtPurchaseCountUp}
              {stockNumCountUp}
            </Typography>
            <Typography variant={outcome_variant} color="red">
              {cashContribCountUp}
            </Typography>
            <Divider sx={{ borderBottomWidth: 2, borderColor: "darkgray" }} />
            <Typography variant={outcome_variant} color="green" fontWeight={"bold"}>
              {valueGainedCountUp}
            </Typography>
            <Button
              variant="contained"
              color="info"
              size="large"
              onClick={handleConfirm}
              startIcon={<AutoFixHigh />}
              sx={{ py: 1, mt: 2 }}
            >
              UPDATE YOUR ESPP Contributions to {ESPPPercent}% for the next offering!
            </Button>
          </Box>
          <Accordion sx={{ mt: 2, borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pb: 0, mb: 0 }}>
              <Typography>More details on how the ESPP works</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>{explainText}</AccordionDetails>
          </Accordion>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignSelf: "flex-end",
              justifyContent: "space-between",
              pt: 2,
              flexGrow: 1,
              gap: isMobile ? 2 : 0,
              alignItems: "flex-end",
            }}
          >
            <Typography variant="caption" color="text" fontStyle="italic">
              *Stock price assumed to be current value for future dates. Actual outcomes and circumstances may vary.
              This is not advice. Please consult a professional.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", maxWidth: 300 }}>
              <Tooltip title="Language settings disabled in sandbox">
                <FormControl sx={{ flex: 1, ml: 1, mr: 1 }}>
                  <TextField
                    select
                    label="Language"
                    size="small"
                    defaultValue={"en"}
                    disabled
                    // value={language}
                    onChange={(e) => {
                      // setLanguage(e.target.value);
                    }}
                    // helperText="Select your preferred language."
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="es">Spanish</MenuItem>
                    <MenuItem value="fr">French</MenuItem>
                    <MenuItem value="de">German</MenuItem>
                    <MenuItem value="zh">Chinese</MenuItem>
                    <MenuItem value="hi">Hindi</MenuItem>
                  </TextField>
                </FormControl>
              </Tooltip>
              <Tooltip title="Currency settings disabled in sandbox">
                <FormControl sx={{ flex: 1, ml: 1 }}>
                  <TextField
                    select
                    label="Currency"
                    size="small"
                    defaultValue={"USD"}
                    disabled
                    // value={currency}
                    onChange={(e) => {
                      // setCurrency(e.target.value);
                    }}
                    // helperText="Select your preferred currency."
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                    <MenuItem value="JPY">JPY</MenuItem>
                    <MenuItem value="CNY">CNY</MenuItem>
                  </TextField>
                </FormControl>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Paper>
      <Box maxWidth={700}>
        <Stack
          sx={{
            display: "flex",
            flexDirection: { xs: "row", lg: "column" },
            justifyContent: "flex-start",
            gap: 0,
            flexShrink: 0,
            overflow: "auto",
          }}
        >
          {[
            {
              inCondition: true,
              boxProps: {
                title:
                  (nextPurchasePeriod === undefined ? "No" : nextPurchasePeriod.enroll_date.diff(dayjs(), "day")) +
                  " days left to enroll",
                description:
                  "To participate in the next offering period, you must enroll by " +
                  (nextPurchasePeriod === undefined
                    ? "an unknown date"
                    : longDateString(nextPurchasePeriod.enroll_date)) +
                  ". Your current paycheck contribution to the ESPP is " +
                  confirmedESPPPercent +
                  "%.",
                buttonText: "Confirm " + ESPPPercent + "% ESPP Contribution",
                urgent: false,
                onClick: handleConfirm,
              },
            },
            {
              inCondition: true,
              boxProps: {
                title: "Did you know?",
                description:
                  "On our last purchase date of Nov 1, 2024, your coworkers enrolled by the Nov 1, 2023 offering period purchased shares of Coinbase with a stock price of $182.88 for just $66.14!",
                buttonText: "See how they got a 64% discount!",
                urgent: true,
                onClick: () => {
                  const offeringDateToFind = dayjs(new Date(2023, 10, 1));
                  const purchaseDateToFind = dayjs(new Date(2024, 10, 1));
                  const selectedPeriod = ESPPPeriods.find(
                    (p) =>
                      p.offering_date.isSame(dayjs(offeringDateToFind)) &&
                      p.purchase_date.isSame(dayjs(purchaseDateToFind))
                  );
                  if (selectedPeriod !== undefined) {
                    setPeriod(selectedPeriod);
                  }
                },
              },
            },
            {
              inCondition: true,
              boxProps: {
                title: "Want to see more?",
                description: "Go to the My ESPPs page to look at multiple ESPP periods and model your contributions!",
                buttonText: "Go to My ESPPs",
                urgent: false,
                onClick: () => {
                  props.setPage("espps");
                },
              },
            },
            {
              inCondition: true,
              boxProps: {
                title: "Questions? Need help?",
                description:
                  "If you have additional questions or need help with your ESPP, please check out additional resources or contact support.",
                buttonText: "Additional Details",
                urgent: false,
                onClick: () => {
                  props.addSnackbarMessage("Additional Details", "Not available in this Sandbox", "info");
                },
              },
            },
          ].map(
            (
              box // TODO: we can choose to show only the first 3 of these or something...factor this out into a function?
            ) => (
              <Collapse
                key={box.boxProps.title}
                in={box.inCondition}
                sx={{
                  flexShrink: 0,
                }}
              >
                <Paper
                  sx={{
                    width: 400,
                    flexShrink: 0,
                    padding: paperPadding,
                    marginBottom: { xs: 0, lg: 2 },
                    marginRight: { xs: 2, lg: 0 },
                    boxSizing: "border-box",
                  }}
                >
                  <CallToActionBox {...box.boxProps} />
                </Paper>
              </Collapse>
            )
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export default ESPPExplorerPanel;
